import React, { useState, useEffect } from 'react';
import { MPPageTitle } from '../../../../components/ui';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import Table from './components/Table/Table';
import InvoiceDisplayer from './components/InvoiceDisplayer/InvoiceDisplayer';
import Margins from './components/Margins/Margins';
import ComprasSearchBar from './components/ComprasSearchBar/ComprasSearchBar';
import FilterBar from './components/FilterBar/FilterBar';
import ComprasStats from './components/Stats/ComprasStats';
import { getAllIngredientPurchasesByLocalIdAndDateRange } from '../../../../crud/management_api/ingredient_purchases';
import { getAllProvidersByLocalId } from '../../../../crud/management_api/providers';
import { exportFilteredCompras } from './utils/comprasExcelExport';
import { toast } from 'react-toastify';
import CreatePurchase from './components/Table/components/CreatePurchase/CreatePurchase';
import { IngredientPurchaseRecord, ExpenseCategoryType } from './types';
import { getAllLocalExpensesCategoryByLocalId } from '../../../../crud/management_api/local_expenses_categories';

interface FilterOption {
  value: string | number;
  label: string;
}

const Gastos: React.FC = () => {
  const [currentView, setCurrentView] = useState<'listado' | 'stack' | 'margins' | 'vendor' | 'estadisticas'>('listado');
  
  const [purchases, setPurchases] = useState<IngredientPurchaseRecord[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<IngredientPurchaseRecord[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [errorPurchases, setErrorPurchases] = useState<string | null>(null);
  
  const [providerFilter, setProviderFilter] = useState<FilterOption[]>([]);
  const [providerOptions, setProviderOptions] = useState<FilterOption[]>([]);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategoryType[]>([]);

  // --- Stats tab: independent state ---
  const [statsPurchases, setStatsPurchases] = useState<IngredientPurchaseRecord[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsProviderId, setStatsProviderId] = useState<string>('');
  
  const formatDateParam = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Listado defaults: 1st of current month → today
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

  const [startDate, setStartDate] = useState(formatDateParam(startOfMonth));
  const [endDate, setEndDate] = useState(formatDateParam(tomorrow));

  // Stats defaults: same — 1st of current month → today
  const [statsStartDate, setStatsStartDate] = useState(formatDateParam(startOfMonth));
  const [statsEndDate, setStatsEndDate] = useState(formatDateParam(tomorrow));

  async function fetchPurchases() {
    setErrorPurchases(null);
    setLoadingPurchases(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      const response = await getAllIngredientPurchasesByLocalIdAndDateRange(localId, startDate, endDate);
      const allData: IngredientPurchaseRecord[] = (response as any).data.reverse();
      // Client-side date guard — ensures data is within the requested range
      const filtered = allData.filter(p => {
        if (!p.purchase_date) return true;
        const d = p.purchase_date.slice(0, 10);
        return d >= startDate && d <= endDate;
      });
      setPurchases(filtered);
      setFilteredPurchases(filtered);
    } catch (err) {
      setErrorPurchases("Error al cargar las compras de ingredientes");
    } finally {
      setLoadingPurchases(false);
    }
  }

  async function fetchStatsPurchases() {
    setLoadingStats(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      const response = await getAllIngredientPurchasesByLocalIdAndDateRange(localId, statsStartDate, statsEndDate);
      const allData: IngredientPurchaseRecord[] = (response as any).data.reverse();
      const filtered = allData.filter(p => {
        if (!p.purchase_date) return true;
        const d = p.purchase_date.slice(0, 10);
        return d >= statsStartDate && d <= statsEndDate;
      });
      setStatsPurchases(filtered);
    } catch (err) {
      console.error("Error fetching stats purchases:", err);
      setStatsPurchases([]);
    } finally {
      setLoadingStats(false);
    }
  }

  async function fetchCategories() {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      const response = await getAllLocalExpensesCategoryByLocalId(localId);
      setCategories(response.data || []);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  }

  async function loadFilterOptions() {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const localId = user.local_id ? Number(user.local_id) : 0;
      const providersResponse = await getAllProvidersByLocalId(localId);
      const providers = providersResponse.data.map((p: any) => ({
        value: p.provider_id,
        label: p.name,
      }));
      setProviderOptions(providers);
    } catch (err) {
      console.error("Error loading filter options:", err);
    }
  }

  const handleExportToExcel = () => {
    const success = exportFilteredCompras(filteredPurchases, startDate, endDate);
    if (success) {
      toast.success("Compras exportadas exitosamente");
    } else {
      toast.error("Error al exportar compras");
    }
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
  };

  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStatsPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsStartDate, statsEndDate]);

  useEffect(() => {
    loadFilterOptions();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = [...purchases];

    if (providerFilter.length > 0) {
      const providerIds = providerFilter.map(p => p.value);
      filtered = filtered.filter(purchase => 
        purchase.provider_id && providerIds.includes(purchase.provider_id)
      );
    }

    setFilteredPurchases(filtered);
  }, [purchases, providerFilter]);

  const views = [
    { key: 'listado' as const, label: 'Listado' },
    { key: 'stack' as const, label: 'Facturas' },
    { key: 'estadisticas' as const, label: 'Estadísticas' },
  ];

  return (
    <div className="flex flex-col gap-4 p-3 lg:gap-5 lg:p-4" style={{ height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      <MPPageTitle>Gastos</MPPageTitle>

      {/* Tab selector */}
      <div className="mp-button-selector-page-container" style={{ padding: 0 }}>
        <div className="mp-button-selector-page-selector">
          {views.map(({ key, label }) => (
            <button
              key={key}
              className={`mp-button-selector-page-button ${currentView === key ? 'active' : 'inactive'}`}
              onClick={() => setCurrentView(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {currentView === 'listado' && (
        <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-hidden">
          <FilterBar
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            providerFilter={providerFilter}
            onProviderFilterChange={setProviderFilter}
            onCreatePurchase={() => setShowCreateModal(true)}
            onExportToExcel={handleExportToExcel}
            totalPurchases={filteredPurchases.length}
            providerOptions={providerOptions}
            categories={categories}
            onCategoriesChanged={fetchCategories}
            searchComponent={
              <ComprasSearchBar
                data={purchases}
                updateFilteredData={setFilteredPurchases}
                placeholder="Buscar compra..."
              />
            }
          />
          <div className="flex-1 min-h-0 overflow-y-auto" style={{ borderRadius: '12px' }}>
            {loadingPurchases ? (
              <LoadingSpinner text="Cargando compras de ingredientes..." transparent={true} />
            ) : errorPurchases ? (
              <div style={{ color: 'red' }}>{errorPurchases}</div>
            ) : filteredPurchases.length > 0 ? (
              <Table data={filteredPurchases} setData={setPurchases} fetchPurchases={fetchPurchases} categories={categories} />
            ) : (
              <h2 style={{ textAlign: 'center', marginTop: '2rem', color:'var(--text-color)', fontSize:'1.2rem' }}>
                ¡No se han registrado compras en este periodo! Para visualizar anteriores utilice los filtros
              </h2>
            )}
          </div>
        </div>
      )}
      {currentView === 'stack' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <InvoiceDisplayer fetchPurchases={fetchPurchases} />
        </div>
      )}
      {currentView === 'margins' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Margins/>
        </div>
      )}
      {currentView === 'estadisticas' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ComprasStats 
            purchases={statsProviderId 
              ? statsPurchases.filter(p => p.provider_id === Number(statsProviderId))
              : statsPurchases
            } 
            loading={loadingStats} 
            categories={categories}
            startDate={statsStartDate}
            endDate={statsEndDate}
            onStartDateChange={setStatsStartDate}
            onEndDateChange={setStatsEndDate}
            selectedProviderId={statsProviderId}
            onProviderChange={setStatsProviderId}
            providerOptions={providerOptions}
          />
        </div>
      )}

      <CreatePurchase
        show={showCreateModal}
        setShow={setShowCreateModal}
        onCreated={fetchPurchases}
        categories={categories}
      />
    </div>
  );
};

export default Gastos;
